import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { remitosApi } from '../api/remitos'
import { zonasApi } from '../api/zonas'
import api from '../api/client'
import { PROVINCIAS, esCapitalFederal, matchProvincia, matchLocalidad } from '../data/argentina'
import CampoLocalidad from '../components/CampoLocalidad'
import CampoDireccion from '../components/CampoDireccion'
import { etiquetaCliente } from '../utils/cliente'
import Boton from '../components/Boton'

// Columnas fijas del modelo de planilla (clave interna → encabezado visible)
const COLS_BASE = [
  { key: 'nombre',            label: 'Nombre *' },
  { key: 'telefono',          label: 'Telefono' },
  { key: 'calle',             label: 'Domicilio' },
  { key: 'provincia',         label: 'Provincia' },
  { key: 'localidad',         label: 'Localidad' },
  { key: 'codigo_postal',     label: 'CP' },
  { key: 'zona',              label: 'Zona' },
  { key: 'fecha_entrega_est', label: 'Fecha entrega (DD/MM/AAAA)' },
  { key: 'hora_desde',        label: 'Hora desde (HH:MM)' },
  { key: 'hora_hasta',        label: 'Hora hasta (HH:MM)' },
  { key: 'notas',             label: 'Observaciones' },
]

const SEP = ';' // Excel en español usa ; como separador de columnas

function csvCampo(v) {
  const s = String(v ?? '')
  return `"${s.replace(/"/g, '""')}"`
}

// Parser CSV simple que respeta comillas y detecta ; o , como separador
function parseCSV(texto) {
  const limpio = texto.replace(/^﻿/, '')
  const sep = (limpio.split('\n')[0].match(/;/g) || []).length >=
              (limpio.split('\n')[0].match(/,/g) || []).length ? ';' : ','
  const filas = []
  let campo = '', fila = [], enComillas = false
  for (let i = 0; i < limpio.length; i++) {
    const c = limpio[i]
    if (enComillas) {
      if (c === '"') {
        if (limpio[i + 1] === '"') { campo += '"'; i++ }
        else enComillas = false
      } else campo += c
    } else if (c === '"') enComillas = true
    else if (c === sep) { fila.push(campo); campo = '' }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && limpio[i + 1] === '\n') i++
      fila.push(campo); filas.push(fila); fila = []; campo = ''
    } else campo += c
  }
  if (campo !== '' || fila.length) { fila.push(campo); filas.push(fila) }
  return filas.filter(f => f.some(c => c.trim() !== ''))
}

// DD/MM/AAAA → YYYY-MM-DD (para el input date). Acepta también ya-ISO.
function fechaAIso(v) {
  const s = String(v || '').trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  const m = s.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{2,4})/)
  if (!m) return ''
  const [, d, mes, a] = m
  const anio = a.length === 2 ? `20${a}` : a
  return `${anio}-${mes.padStart(2, '0')}-${d.padStart(2, '0')}`
}

const filaVacia = (articulos) => ({
  _key: Math.random().toString(36).slice(2),
  nombre: '', telefono: '', calle: '',
  localidad: '', provincia: '', codigo_postal: '', notas: '',
  zona_id: '', fecha_entrega_est: '', hora_desde: '', hora_hasta: '',
  articulos: (articulos || []).map(a => ({ ...a, cantidad: '' })),
})

export default function PlanillaObsequiadosPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [remito, setRemito] = useState(null)
  const [zonas, setZonas] = useState([])
  const [filas, setFilas] = useState([])
  const [articulosBase, setArticulosBase] = useState([])
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState('')
  const [descuadre, setDescuadre] = useState([])
  const [importMsg, setImportMsg] = useState('')
  const fileInputRef = useRef(null)

  useEffect(() => {
    Promise.all([
      remitosApi.obtener(id),
      zonasApi.listar(),
    ]).then(([{ data: r }, { data: z }]) => {
      setRemito(r)
      setZonas(Array.isArray(z) ? z : (z.data || []))
      // Stock restante = total del remito - lo ya asignado en NEs existentes
      const yaAsignado = {}
      ;(r.notas_envio || []).forEach(ne => {
        ;(ne.articulos || []).forEach(a => {
          yaAsignado[a.articulo_id] = (yaAsignado[a.articulo_id] || 0) + Number(a.cantidad || 0)
        })
      })
      // Renglones fiscales (envío cotizado, gift card, uso interno): no son
      // carga — no se piden en la planilla (flag del backend, regla única).
      const arts = (r.articulos || []).filter(ra => !ra.oculto_impresos).map(ra => ({
        articulo_id: ra.articulo_id,
        codigo_alfa: ra.articulo?.codigo_alfa,
        descripcion: ra.articulo?.descripcion,
        totalRemito: Number(ra.cantidad),
        yaAsignado: yaAsignado[ra.articulo_id] || 0,
        max: Math.max(0, Number(ra.cantidad) - (yaAsignado[ra.articulo_id] || 0)),
      }))
      setArticulosBase(arts)
      // Si ya no queda stock para repartir, no inicializamos fila vacía
      const restanteTotal = arts.reduce((acc, a) => acc + a.max, 0)
      // Envío múltiple: los destinos ya cargados en la NV precargan la planilla
      // (receptor, dirección, notas y artículos por destino). Solo cuando la
      // planilla está vacía (sin NE previas) — docs/diseno-envio-multiple.md.
      const precarga = (r.envio_destinos_planilla || [])
      if (restanteTotal > 0 && (r.notas_envio || []).length === 0 && precarga.length > 0) {
        setFilas(precarga.map(d => ({
          ...filaVacia(arts),
          nombre: d.nombre || '', telefono: d.telefono || '', calle: d.calle || '',
          localidad: d.localidad || '', provincia: d.provincia || '', codigo_postal: d.codigo_postal || '', notas: d.notas || '',
          articulos: arts.map(a => ({ ...a, cantidad: (d.articulos || []).find(x => x.articulo_id === a.articulo_id)?.cantidad || '' })),
        })))
        setImportMsg(`Planilla precargada con los ${precarga.length} destinos de la nota de venta. Revisá zonas y cantidades antes de guardar.`)
      } else {
        setFilas(restanteTotal > 0 ? [filaVacia(arts)] : [])
      }
    }).catch(() => navigate('/remitos'))
  }, [id, navigate])

  // ---- totales y validación ----
  const totales = articulosBase.reduce((acc, a) => {
    acc[a.articulo_id] = { asignado: 0, max: a.max, codigo_alfa: a.codigo_alfa, descripcion: a.descripcion }
    return acc
  }, {})
  filas.forEach(f => {
    f.articulos.forEach(a => {
      const t = totales[a.articulo_id]
      if (!t) return
      t.asignado += Number(a.cantidad) || 0
    })
  })
  const cierra = Object.values(totales).every(t => t.asignado === t.max)
  const totalUnidadesPedidas = Object.values(totales).reduce((acc, t) => acc + t.max, 0)
  const totalUnidadesAsignadas = Object.values(totales).reduce((acc, t) => acc + t.asignado, 0)
  const porcentaje = totalUnidadesPedidas === 0 ? 100 : Math.round((totalUnidadesAsignadas / totalUnidadesPedidas) * 100)

  // ---- mutaciones ----
  const setFila = useCallback((idx, campo, valor) => {
    setFilas(prev => prev.map((f, i) => i === idx ? { ...f, [campo]: valor } : f))
  }, [])

  const setCantArticulo = useCallback((idx, articuloId, valor) => {
    setFilas(prev => prev.map((f, i) => {
      if (i !== idx) return f
      return {
        ...f,
        articulos: f.articulos.map(a => a.articulo_id === articuloId
          ? { ...a, cantidad: valor === '' ? '' : Math.max(0, Number(valor)) }
          : a),
      }
    }))
  }, [])

  const agregarFila = () => setFilas(prev => [...prev, filaVacia(articulosBase)])

  const duplicarUltimo = () => {
    const last = filas[filas.length - 1] || filaVacia(articulosBase)
    setFilas(prev => [...prev, { ...last, _key: Math.random().toString(36).slice(2), nombre: '', telefono: '', notas: '', articulos: last.articulos.map(a => ({ ...a, cantidad: '' })) }])
  }

  const eliminarFila = (idx) => {
    if (filas.length === 1) return
    setFilas(prev => prev.filter((_, i) => i !== idx))
  }

  // Repartir todo el stock pendiente parejo entre los destinatarios cargados.
  // Se reparte por artículo: piso(restante/N) a cada uno + el resto al primero/s.
  const repartirParejo = () => {
    if (filas.length === 0) return
    setFilas(prev => {
      const nuevos = prev.map(f => ({ ...f, articulos: f.articulos.map(a => ({ ...a, cantidad: 0 })) }))
      articulosBase.forEach(a => {
        const restante = a.max
        const base = Math.floor(restante / filas.length)
        let sobra = restante - base * filas.length
        nuevos.forEach((dest) => {
          const target = dest.articulos.find(x => x.articulo_id === a.articulo_id)
          if (!target) return
          target.cantidad = base + (sobra > 0 ? 1 : 0)
          if (sobra > 0) sobra--
        })
      })
      return nuevos
    })
  }

  // Repartir parejo SOLO un artículo entre los destinatarios.
  const repartirArticuloParejo = (articuloId) => {
    if (filas.length === 0) return
    const art = articulosBase.find(a => a.articulo_id === articuloId)
    if (!art) return
    const base = Math.floor(art.max / filas.length)
    let sobra = art.max - base * filas.length
    setFilas(prev => prev.map((dest) => ({
      ...dest,
      articulos: dest.articulos.map(a => {
        if (a.articulo_id !== articuloId) return a
        const extra = sobra > 0 ? 1 : 0
        if (sobra > 0) sobra--
        return { ...a, cantidad: base + extra }
      }),
    })))
  }

  // Vaciar todas las cantidades de un artículo (en todos los destinatarios).
  const limpiarArticulo = (articuloId) => {
    setFilas(prev => prev.map(dest => ({
      ...dest,
      articulos: dest.articulos.map(a => a.articulo_id === articuloId ? { ...a, cantidad: '' } : a),
    })))
  }

  // Asignar todo el stock restante (lo no asignado en otras filas) a esta fila.
  const asignarRestoA = (idx) => {
    setFilas(prev => prev.map((f, i) => {
      if (i !== idx) return f
      return {
        ...f,
        articulos: f.articulos.map(a => {
          const total = totales[a.articulo_id]
          if (!total) return a
          const otrasFilas = prev.reduce((acc, fOtra, j) => {
            if (j === idx) return acc
            const x = fOtra.articulos.find(x => x.articulo_id === a.articulo_id)
            return acc + (Number(x?.cantidad) || 0)
          }, 0)
          return { ...a, cantidad: Math.max(0, total.max - otrasFilas) }
        }),
      }
    }))
  }

  const limpiarCantidades = (idx) => {
    setFilas(prev => prev.map((f, i) => i === idx ? { ...f, articulos: f.articulos.map(a => ({ ...a, cantidad: '' })) } : f))
  }

  // ---- localidad → zona auto ----
  const resolverZona = async (idx, localidad) => {
    if (!localidad || !localidad.trim()) return
    try {
      const { data } = await api.get('/zona-localidades', { params: { localidad: localidad.trim() } })
      const items = Array.isArray(data) ? data : (data.data || [])
      const match = items.find(zl => zl.localidad?.toLowerCase() === localidad.toLowerCase()) || items[0]
      if (match?.zona_id) setFila(idx, 'zona_id', String(match.zona_id))
    } catch { /* silent */ }
  }

  const handleLocalidad = async (idx, localidad) => {
    setFila(idx, 'localidad', localidad)
    await resolverZona(idx, localidad)
  }

  // Autocompletado de Google Places por fila: rellena calle/localidad/provincia/CP
  // de ESA fila en un solo update y dispara la auto-zona por la localidad resuelta.
  const handlePlaceFila = (idx, d) => {
    setFilas(prev => prev.map((f, i) => i === idx ? {
      ...f,
      // calle_completa conserva el piso/depto tipeado tras la altura.
      calle: d.calle_completa || d.calle || f.calle,
      localidad: d.localidad || f.localidad,
      provincia: d.provincia || f.provincia,
      codigo_postal: d.codigo_postal || f.codigo_postal,
    } : f))
    if (d.localidad) resolverZona(idx, d.localidad)
  }

  // ---- descargar modelo Excel/CSV ----
  // Genera un CSV (lo abre Excel nativo) con una columna por cada artículo del remito.
  const descargarModelo = () => {
    const colsArt = articulosBase.map(a => `${a.codigo_alfa} (max ${a.max})`)
    const encabezados = [...COLS_BASE.map(c => c.label), ...colsArt]

    // Fila de ejemplo para guiar la carga
    const ejemplo = [
      'Juan Pérez', '11-2345-6789', 'Av. Corrientes 1234 Piso 3 B',
      'Buenos Aires', 'Ramos Mejía', '1704', '', '15/06/2026', '09:00', '13:00',
      'Tocar timbre',
      ...articulosBase.map(() => ''),
    ]

    const lineas = [
      encabezados.map(csvCampo).join(SEP),
      ejemplo.map(csvCampo).join(SEP),
    ]
    const csv = '﻿' + lineas.join('\r\n') // BOM para que Excel lea acentos
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `planilla_${remito.numero_presea || id}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ---- importar archivo ----
  const importarArchivo = async (file) => {
    setError(''); setImportMsg('')
    if (!file) return
    try {
      const texto = await file.text()
      const filas = parseCSV(texto)
      if (filas.length < 2) { setError('El archivo está vacío o no tiene filas de datos.'); return }

      const headers = filas[0].map(h => h.trim())
      // Índices de columnas base por su etiqueta exacta
      const idxBase = {}
      COLS_BASE.forEach(c => { idxBase[c.key] = headers.findIndex(h => h === c.label) })
      // Índices de columnas de artículos: matchea por código al inicio del encabezado
      const idxArt = articulosBase.map(a => ({
        articulo_id: a.articulo_id,
        idx: headers.findIndex(h => h.trim().toUpperCase().startsWith(String(a.codigo_alfa).toUpperCase())),
      }))

      const nuevasFilas = filas.slice(1).map(cols => {
        const val = (k) => idxBase[k] >= 0 ? (cols[idxBase[k]] || '').trim() : ''
        // resolver zona por nombre
        const zonaNombre = val('zona')
        const zonaMatch = zonas.find(z => z.nombre?.toLowerCase() === zonaNombre.toLowerCase())
        // Normalizamos provincia/localidad a la opción canónica (case+tildes
        // insensible) para que los <select> muestren el valor importado y no
        // queden en blanco — ver sugerencia #24.
        const provincia = matchProvincia(val('provincia'))
        const localidad = esCapitalFederal(provincia)
          ? ''
          : (provincia === 'Buenos Aires' ? matchLocalidad(val('localidad')) : val('localidad'))
        return {
          _key: Math.random().toString(36).slice(2),
          nombre: val('nombre'),
          telefono: val('telefono'),
          calle: val('calle'),
          provincia,
          localidad,
          codigo_postal: val('codigo_postal'),
          zona_id: zonaMatch ? String(zonaMatch.id) : '',
          fecha_entrega_est: fechaAIso(val('fecha_entrega_est')),
          hora_desde: val('hora_desde').slice(0, 5),
          hora_hasta: val('hora_hasta').slice(0, 5),
          notas: val('notas'),
          articulos: articulosBase.map(a => {
            const col = idxArt.find(x => x.articulo_id === a.articulo_id)
            const raw = col && col.idx >= 0 ? (cols[col.idx] || '').trim() : ''
            const cant = raw === '' ? '' : Math.max(0, Number(raw) || 0)
            return { articulo_id: a.articulo_id, codigo_alfa: a.codigo_alfa, descripcion: a.descripcion, cantidad: cant }
          }),
        }
      }).filter(f => f.nombre || f.articulos.some(a => Number(a.cantidad) > 0))

      if (nuevasFilas.length === 0) { setError('No se encontraron destinatarios válidos en el archivo.'); return }
      setFilas(nuevasFilas)
      setImportMsg(`✓ Se importaron ${nuevasFilas.length} destinatario${nuevasFilas.length !== 1 ? 's' : ''}. Revisá las cantidades y guardá.`)
    } catch (err) {
      setError('No se pudo leer el archivo. Asegurate de que sea el CSV del modelo.')
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  // ---- guardar ----
  const handleGuardar = async () => {
    if (!cierra) { setError('Los totales no cierran. Revisá el panel de stock.'); return }
    setGuardando(true)
    setError('')
    setDescuadre([])
    const destinatarios = filas.map(f => ({
      nombre: f.nombre,
      telefono: f.telefono || null,
      calle: f.calle || null,
      numero: null, piso: null, depto: null,
      localidad: f.localidad || null,
      provincia: f.provincia || null,
      codigo_postal: f.codigo_postal || null,
      notas: f.notas || null,
      zona_id: f.zona_id ? parseInt(f.zona_id) : null,
      fecha_entrega_est: f.fecha_entrega_est || null,
      hora_desde: f.hora_desde || null,
      hora_hasta: f.hora_hasta || null,
      articulos: f.articulos.filter(a => a.cantidad > 0).map(a => ({ articulo_id: a.articulo_id, cantidad: a.cantidad })),
    }))
    try {
      await remitosApi.generarNe(id, { destinatarios })
      navigate(`/remitos/${id}`)
    } catch (err) {
      if (err.response?.data?.descuadre) {
        setDescuadre(err.response.data.descuadre)
        setError(err.response.data.message || 'Los totales no cierran.')
      } else {
        setError(err.response?.data?.message || 'Error al generar las notas de envío.')
      }
    } finally { setGuardando(false) }
  }

  if (!remito) return <p style={{ color: '#64748b' }}>Cargando...</p>

  const stockRestanteTotal = articulosBase.reduce((acc, a) => acc + a.max, 0)
  const todoAsignado = articulosBase.length > 0 && stockRestanteTotal === 0
  const nesExistentes = remito.notas_envio || []

  return (
    <div style={st.wrap}>
      {/* Header */}
      <div style={st.headerRow}>
        <div>
          <h2 style={st.title}>Planilla de envío</h2>
          <p style={st.subtitle}>
            Remito <code>{remito.numero_presea}</code> · {etiquetaCliente(remito.cliente)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' }}>
          {!todoAsignado && (
            <>
              <Boton variante="secondary" onClick={descargarModelo} type="button" title="Descargá un Excel/CSV con una columna por artículo y completalo">
                ⬇ Descargar modelo
              </Boton>
              <Boton variante="secondary" onClick={() => fileInputRef.current?.click()} type="button" title="Subí el archivo completado para llenar la planilla">
                ⬆ Subir planilla
              </Boton>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={e => importarArchivo(e.target.files?.[0])}
              />
            </>
          )}
          <Boton variante="ghost" onClick={() => navigate(-1)}>← Volver</Boton>
        </div>
      </div>

      {importMsg && <div style={st.importMsg}>{importMsg}</div>}

      {/* NEs ya generadas */}
      {nesExistentes.length > 0 && (
        <div style={st.nesGeneradas}>
          <div style={st.nesHeader}>
            <h3 style={st.nesTitle}>Planillas / NEs ya generadas ({nesExistentes.length})</h3>
            {!todoAsignado && (
              <span style={st.nesHint}>Las cantidades de abajo son sobre el stock <strong>restante</strong>.</span>
            )}
          </div>
          <div style={st.nesList}>
            {nesExistentes.map(ne => (
              <button key={ne.id} type="button" style={st.neItem} onClick={() => navigate(`/notas-envio/${ne.id}`)}>
                <span style={st.neNumero}><code>{ne.numero_ne}</code></span>
                <span style={st.neDest}>{ne.destinatario?.nombre || '(sin nombre)'}</span>
                {ne.zona?.nombre && <span style={st.neZona}>{ne.zona.nombre}</span>}
                <span style={st.neArrow}>→</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Banner: todo el stock ya fue asignado */}
      {todoAsignado && (
        <div style={st.todoAsignadoBanner}>
          <div>
            <strong>✓ Todas las unidades del remito ya están asignadas a NEs.</strong>
            <div style={{ fontSize: '0.8125rem', color: '#475569', marginTop: '0.25rem' }}>
              Para repartir nuevamente tenés que anular/eliminar alguna de las NEs existentes desde su detalle.
            </div>
          </div>
          <Boton variante="primary" type="button" onClick={() => navigate(`/remitos/${id}`)}>
            Volver al remito →
          </Boton>
        </div>
      )}

      {/* Stock panel - sticky (solo si queda algo por asignar) */}
      {!todoAsignado && (
      <>
      <div style={st.stockBar}>
        <div style={st.stockLeft}>
          <div style={st.stockTitle}>Stock a asignar</div>
          <div style={st.chipsRow}>
            {Object.values(totales).map(t => {
              const ok = t.asignado === t.max
              const excede = t.asignado > t.max
              const chipStyle = excede ? st.chipExcede : ok ? st.chipOk : st.chipFalta
              return (
                <div key={t.codigo_alfa} style={{ ...st.chip, ...chipStyle }} title={t.descripcion}>
                  <span style={st.chipCodigo}>{t.codigo_alfa}</span>
                  <span style={st.chipNum}>{t.asignado} / {t.max}</span>
                </div>
              )
            })}
          </div>
        </div>
        <div style={st.stockRight}>
          <div style={st.progress}>
            <div style={{ ...st.progressFill, width: `${Math.min(porcentaje, 100)}%`, background: porcentaje === 100 ? '#16a34a' : '#3b82f6' }} />
          </div>
          <div style={st.progressLabel}>
            {totalUnidadesAsignadas} / {totalUnidadesPedidas} unidades ({porcentaje}%)
          </div>
          <div style={st.acciones}>
            <Boton variante="secondary" sm onClick={agregarFila} type="button">+ Destinatario</Boton>
            <Boton variante="secondary" sm onClick={duplicarUltimo} type="button" disabled={filas.length === 0}>Duplicar último</Boton>
            <Boton variante="secondary" sm onClick={repartirParejo} type="button" disabled={filas.length === 0}>⚖ Repartir parejo</Boton>
          </div>
        </div>
      </div>

      {error && <div style={st.errorBox}>{error}</div>}

      {descuadre.length > 0 && (
        <div style={st.descuadreBox}>
          <strong>Descuadre por artículo:</strong>
          <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem' }}>
            {descuadre.map(d => (
              <li key={d.articulo_id} style={{ fontSize: '0.875rem' }}>
                Artículo #{d.articulo_id}: esperado {d.esperado}, asignado {d.asignado}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* === DISTRIBUCIÓN DE ARTÍCULOS === */}
      <div style={st.distribCard}>
        <div style={st.distribHeader}>
          <h3 style={st.distribTitle}>Distribución de artículos</h3>
          <span style={st.distribHint}>
            {filas.length} destinatario{filas.length !== 1 ? 's' : ''} cargado{filas.length !== 1 ? 's' : ''}
          </span>
        </div>

        {filas.length === 0 ? (
          <p style={st.empty}>Agregá al menos un destinatario abajo para empezar a repartir.</p>
        ) : (
          <div style={st.distribList}>
            {articulosBase.map(art => {
              const t = totales[art.articulo_id]
              const ok = t.asignado === t.max
              const excede = t.asignado > t.max
              const restante = t.max - t.asignado
              return (
                <div key={art.articulo_id} style={st.artBlock}>
                  <div style={st.artBlockHeader}>
                    <div style={st.artInfo}>
                      <code style={st.artCodigo}>{art.codigo_alfa}</code>
                      <span style={st.artDesc}>{art.descripcion}</span>
                    </div>
                    <div style={st.artHeaderRight}>
                      <span style={{
                        ...st.artChip,
                        ...(excede ? st.artChipExcede : ok ? st.artChipOk : st.artChipFalta),
                      }}>
                        {t.asignado} / {t.max}
                        {!ok && !excede && ` · faltan ${restante}`}
                        {excede && ` · exceso ${-restante}`}
                      </span>
                      <Boton variante="secondary" sm type="button" onClick={() => repartirArticuloParejo(art.articulo_id)} title="Distribuir este artículo entre los destinatarios">⚖ Repartir</Boton>
                      <Boton variante="ghost" sm type="button" onClick={() => limpiarArticulo(art.articulo_id)} title="Vaciar este artículo">⌫</Boton>
                    </div>
                  </div>
                  <div style={st.distribRow}>
                    {filas.map((f, idx) => {
                      const item = f.articulos.find(x => x.articulo_id === art.articulo_id)
                      const cant = item?.cantidad ?? ''
                      const label = f.nombre?.trim() || `Destinatario ${idx + 1}`
                      return (
                        <div key={f._key} style={st.distribCell}>
                          <span style={st.distribCellLabel} title={label}>
                            <span style={st.distribCellNum}>#{idx + 1}</span>
                            <span style={st.distribCellName}>{label}</span>
                          </span>
                          <input
                            type="number"
                            min="0"
                            max={art.max}
                            value={cant}
                            onChange={e => setCantArticulo(idx, art.articulo_id, e.target.value)}
                            placeholder="0"
                            style={{
                              ...st.distribInput,
                              borderColor: excede ? '#dc2626' : '#cbd5e1',
                              background: excede ? '#fef2f2' : 'white',
                            }}
                          />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* === DATOS DE DESTINATARIOS === */}
      <div style={st.sectionHeader}>
        <h3 style={st.sectionTitle}>Datos de destinatarios</h3>
      </div>
      <div style={st.destList}>
        {filas.map((fila, idx) => {
          // Provincia canónica para que el <select> muestre el valor aunque
          // venga en otra capitalización/tildes (CSV, carga manual) — #24.
          const provinciaCanon = matchProvincia(fila.provincia)
          const esCabaP = esCapitalFederal(provinciaCanon)
          const totalFila = fila.articulos.reduce((acc, a) => acc + (Number(a.cantidad) || 0), 0)
          const vacia = totalFila === 0
          return (
            <div key={fila._key} style={{ ...st.destCard, ...(vacia ? st.destCardVacia : {}) }}>
              <div style={st.destHeader}>
                <div style={st.destTitle}>
                  <span style={st.destNum}>#{idx + 1}</span>
                  <span style={st.destNombrePreview}>{fila.nombre || <em style={{ color: '#94a3b8' }}>(sin nombre)</em>}</span>
                  <span style={st.destBadge}>{totalFila} unid.</span>
                </div>
                <div style={st.destActions}>
                  <Boton variante="secondary" sm type="button" onClick={() => asignarRestoA(idx)} title="Asignar todo lo pendiente a este destinatario">⚡ Resto acá</Boton>
                  <Boton variante="secondary" sm type="button" onClick={() => limpiarCantidades(idx)} title="Vaciar cantidades">⌫ Limpiar</Boton>
                  <Boton variante="destructive" sm type="button" onClick={() => eliminarFila(idx)} disabled={filas.length === 1}>✕</Boton>
                </div>
              </div>

              {/* Datos del destinatario - grid compacto */}
              <div style={st.gridDatos}>
                <div>
                  <label style={st.label}>Nombre *</label>
                  <input style={st.input} value={fila.nombre} onChange={e => setFila(idx, 'nombre', e.target.value)} placeholder="Nombre y apellido / Razón social" />
                </div>
                <div>
                  <label style={st.label}>Teléfono</label>
                  <input style={st.input} value={fila.telefono} onChange={e => setFila(idx, 'telefono', e.target.value)} placeholder="11-1234-5678" />
                </div>
              </div>

              <div>
                <label style={st.label}>Domicilio (calle, número, piso, depto)</label>
                <CampoDireccion
                  value={fila.calle}
                  onChange={v => setFila(idx, 'calle', v)}
                  onPlaceSelected={d => handlePlaceFila(idx, d)}
                  inputStyle={st.input}
                  placeholder="Av. Corrientes 1234 Piso 3 Depto B"
                />
              </div>

              <div style={st.gridUbic}>
                <div>
                  <label style={st.label}>Provincia *</label>
                  <select style={st.input} value={provinciaCanon} onChange={e => setFila(idx, 'provincia', e.target.value)}>
                    <option value="">— Seleccionar —</option>
                    {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                {!esCabaP && (
                  <div>
                    <label style={st.label}>Localidad</label>
                    <CampoLocalidad
                      provincia={provinciaCanon}
                      value={fila.localidad}
                      onChange={v => handleLocalidad(idx, v)}
                      inputStyle={st.input}
                      cp={fila.codigo_postal}
                    />
                  </div>
                )}
                <div>
                  <label style={st.label}>C.P.{esCabaP ? ' *' : ''}</label>
                  <input style={st.input} value={fila.codigo_postal} onChange={e => setFila(idx, 'codigo_postal', e.target.value)} />
                </div>
                <div>
                  <label style={st.label}>Zona</label>
                  <select style={st.input} value={fila.zona_id} onChange={e => setFila(idx, 'zona_id', e.target.value)}>
                    <option value="">— Sin zona —</option>
                    {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
                  </select>
                </div>
              </div>

              <div style={st.gridFechaHora}>
                <div>
                  <label style={st.label}>Fecha entrega</label>
                  <input style={st.input} type="date" value={fila.fecha_entrega_est} onChange={e => setFila(idx, 'fecha_entrega_est', e.target.value)} />
                </div>
                <div>
                  <label style={st.label}>Hora desde</label>
                  <input style={st.input} type="time" value={fila.hora_desde} onChange={e => setFila(idx, 'hora_desde', e.target.value)} />
                </div>
                <div>
                  <label style={st.label}>Hora hasta</label>
                  <input style={st.input} type="time" value={fila.hora_hasta} onChange={e => setFila(idx, 'hora_hasta', e.target.value)} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={st.label}>Observaciones</label>
                  <input style={st.input} value={fila.notas} onChange={e => setFila(idx, 'notas', e.target.value)} placeholder="Notas para la entrega" />
                </div>
              </div>

              {/* Resumen compacto de cantidades (read-only — se edita en panel arriba) */}
              {totalFila > 0 && (
                <div style={st.resumenChips}>
                  {fila.articulos.filter(a => Number(a.cantidad) > 0).map(a => (
                    <span key={a.articulo_id} style={st.resumenChip} title={a.descripcion}>
                      <code style={{ fontSize: '0.7rem' }}>{a.codigo_alfa}</code> × {a.cantidad}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Footer acciones */}
      <div style={st.footer}>
        <Boton variante="secondary" onClick={agregarFila} type="button">+ Agregar destinatario</Boton>
        <Boton
          variante="primary"
          disabled={!cierra || guardando}
          onClick={handleGuardar}
        >
          {guardando
            ? 'Generando NE...'
            : cierra
              ? `✓ Generar ${filas.length} nota${filas.length !== 1 ? 's' : ''} de envío`
              : `Faltan ${totalUnidadesPedidas - totalUnidadesAsignadas} unidades por asignar`}
        </Boton>
      </div>
      </>
      )}
    </div>
  )
}

const st = {
  wrap: { maxWidth: '1100px', paddingBottom: '4rem' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', gap: '1rem' },
  title: { margin: 0, fontSize: '1.25rem', color: '#1e293b' },
  subtitle: { margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#64748b' },
  importMsg: { background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.625rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.875rem' },

  // Sticky stock bar
  stockBar: {
    position: 'sticky', top: '1rem', zIndex: 20,
    background: 'white', borderRadius: '10px', padding: '0.875rem 1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0',
    marginBottom: '1rem',
    display: 'grid', gridTemplateColumns: '1fr auto', gap: '1.25rem', alignItems: 'center',
  },
  stockLeft: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  stockTitle: { fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  chipsRow: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  chip: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.75rem', borderRadius: '20px', border: '1.5px solid', fontWeight: 600, fontSize: '0.8125rem' },
  chipCodigo: { fontFamily: 'monospace', fontSize: '0.8125rem' },
  chipNum: { fontWeight: 800 },
  chipOk: { background: '#dcfce7', borderColor: '#86efac', color: '#166534' },
  chipFalta: { background: '#fef3c7', borderColor: '#fcd34d', color: '#92400e' },
  chipExcede: { background: '#fee2e2', borderColor: '#fca5a5', color: '#991b1b' },

  stockRight: { display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '260px' },
  progress: { height: '6px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' },
  progressFill: { height: '100%', transition: 'width 0.2s ease' },
  progressLabel: { fontSize: '0.75rem', color: '#475569', fontWeight: 600, textAlign: 'right' },
  acciones: { display: 'flex', gap: '0.375rem', justifyContent: 'flex-end', flexWrap: 'wrap' },

  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem' },
  descuadreBox: { background: '#fff7ed', border: '1px solid #fed7aa', color: '#92400e', padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.875rem' },

  // Lista de destinatarios
  destList: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  destCard: { background: 'white', borderRadius: '10px', padding: '0.875rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '0.625rem' },
  destCardVacia: { borderStyle: 'dashed', borderColor: '#cbd5e1', background: '#fafafa' },
  destHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' },
  destTitle: { display: 'flex', alignItems: 'center', gap: '0.625rem' },
  destNum: { fontWeight: 800, fontSize: '0.95rem', color: '#1e293b', background: '#f1f5f9', padding: '0.15rem 0.6rem', borderRadius: '12px' },
  destNombrePreview: { fontWeight: 600, color: '#475569', fontSize: '0.9rem' },
  destBadge: { fontSize: '0.7rem', fontWeight: 700, background: '#eff6ff', color: '#1e40af', padding: '0.15rem 0.5rem', borderRadius: '10px', textTransform: 'uppercase', letterSpacing: '0.03em' },
  destActions: { display: 'flex', gap: '0.375rem' },

  // Grids dentro de la card
  gridDatos: { display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.625rem' },
  gridUbic: { display: 'grid', gridTemplateColumns: '1fr 1fr 0.6fr 1fr', gap: '0.625rem' },
  gridFechaHora: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.625rem' },

  label: { display: 'block', fontWeight: 600, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.2rem' },
  input: { width: '100%', padding: '0.4rem 0.625rem', border: '1px solid #d1d5db', borderRadius: '5px', fontSize: '0.8125rem', boxSizing: 'border-box' },

  // Panel de distribución de artículos
  distribCard: { background: 'white', borderRadius: '10px', padding: '1rem 1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '1rem' },
  distribHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' },
  distribTitle: { margin: 0, fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b' },
  distribHint: { fontSize: '0.75rem', color: '#64748b', fontWeight: 600 },
  distribList: { display: 'flex', flexDirection: 'column', gap: '0.625rem' },
  empty: { color: '#94a3b8', fontSize: '0.875rem', margin: 0 },

  artBlock: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0.75rem 0.875rem' },
  artBlockHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem', flexWrap: 'wrap', gap: '0.5rem' },
  artInfo: { display: 'flex', flexDirection: 'column', gap: '0.15rem', minWidth: 0 },
  artCodigo: { fontWeight: 800, color: '#1e293b', background: '#e0e7ff', padding: '0.1rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', alignSelf: 'flex-start' },
  artDesc: { fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' },
  artHeaderRight: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' },
  artChip: { padding: '0.2rem 0.625rem', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.03em', border: '1.5px solid' },
  artChipOk: { background: '#dcfce7', color: '#166534', borderColor: '#86efac' },
  artChipFalta: { background: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' },
  artChipExcede: { background: '#fee2e2', color: '#991b1b', borderColor: '#fca5a5' },

  distribRow: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  distribCell: { display: 'flex', flexDirection: 'column', gap: '0.25rem', background: 'white', padding: '0.5rem 0.625rem', borderRadius: '6px', border: '1px solid #e2e8f0', minWidth: '110px', flex: '0 0 auto' },
  distribCellLabel: { display: 'flex', alignItems: 'center', gap: '0.3rem', maxWidth: '140px' },
  distribCellNum: { fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', background: '#f1f5f9', padding: '0.05rem 0.3rem', borderRadius: '4px' },
  distribCellName: { fontSize: '0.75rem', color: '#475569', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  distribInput: { width: '100%', padding: '0.35rem 0.4rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.95rem', fontWeight: 700, textAlign: 'center', boxSizing: 'border-box' },

  // Header de la sección de destinatarios
  sectionHeader: { marginBottom: '0.625rem' },
  sectionTitle: { margin: 0, fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b' },

  // Resumen de cantidades en cada card de destinatario (read-only)
  resumenChips: { display: 'flex', gap: '0.375rem', flexWrap: 'wrap', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', padding: '0.4rem 0.6rem' },
  resumenChip: { fontSize: '0.75rem', color: '#166534', fontWeight: 600, background: 'white', padding: '0.15rem 0.5rem', borderRadius: '12px', border: '1px solid #bbf7d0' },

  // Footer
  footer: { marginTop: '1rem', padding: '1rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', borderTop: '1px solid #e2e8f0' },

  // NEs ya generadas
  nesGeneradas: { background: 'white', borderRadius: '10px', padding: '0.875rem 1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0', marginBottom: '1rem' },
  nesHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem', flexWrap: 'wrap', gap: '0.5rem' },
  nesTitle: { margin: 0, fontSize: '0.9375rem', fontWeight: 700, color: '#1e293b' },
  nesHint: { fontSize: '0.75rem', color: '#64748b' },
  nesList: { display: 'flex', flexDirection: 'column', gap: '0.375rem' },
  neItem: { display: 'flex', alignItems: 'center', gap: '0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0.5rem 0.75rem', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' },
  neNumero: { fontFamily: 'monospace', fontWeight: 700, color: '#1e293b', fontSize: '0.8125rem', flexShrink: 0, minWidth: '120px' },
  neDest: { fontSize: '0.875rem', color: '#1e293b', fontWeight: 600, flex: 1 },
  neZona: { fontSize: '0.75rem', color: '#475569', background: '#dbeafe', padding: '0.1rem 0.5rem', borderRadius: '10px', fontWeight: 600 },
  neArrow: { color: '#94a3b8', fontWeight: 700 },

  todoAsignadoBanner: { background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.875rem 1rem', borderRadius: '8px', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' },
}
